import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

// Color map based on CSS variables
const COLORS = {
  Brand: "#22c55e",
  "Demand Creation": "#3b82f6",
  "Sales Enablement": "#f59e0b",
  "Market Intelligence": "#8b5cf6",
};

const ProgramMixPlanner = () => {
  // ---- State ----
  const [budget, setBudget] = useState(100000);

  // Mix Percentages
  const [mix, setMix] = useState({
    Brand: 30,
    "Demand Creation": 30,
    "Sales Enablement": 20,
    "Market Intelligence": 20,
  });

  // Unit Costs
  const [costs, setCosts] = useState({
    brandCPM: 12,
    demandCPL: 50,
    enablementPerContact: 25,
    intelPerSignal: 15,
  });

  const [quarters, setQuarters] = useState({
    q1: 100000,
    q2: 100000,
    q3: 100000,
    q4: 100000,
  });
  const [lockQuarters, setLockQuarters] = useState(true);

  // ---- Handlers ----
  const handleMixChange = (key, val) => {
    setMix((prev) => ({ ...prev, [key]: Number(val) }));
  };

  const handleCostChange = (key, val) => {
    setCosts((prev) => ({ ...prev, [key]: Number(val) }));
  };

  const handleBudgetChange = (val) => {
    const newBudget = Number(val);
    setBudget(newBudget);
    if (lockQuarters) {
      setQuarters({
        q1: newBudget,
        q2: newBudget,
        q3: newBudget,
        q4: newBudget,
      });
    }
  };

  // ---- Calculations ----
  const allocated = useMemo(() => {
    return {
      Brand: budget * (mix.Brand / 100),
      "Demand Creation": budget * (mix["Demand Creation"] / 100),
      "Sales Enablement": budget * (mix["Sales Enablement"] / 100),
      "Market Intelligence": budget * (mix["Market Intelligence"] / 100),
    };
  }, [budget, mix]);

  const outputs = useMemo(() => {
    return {
      Brand: (allocated.Brand / costs.brandCPM) * 1000,
      "Demand Creation": allocated["Demand Creation"] / costs.demandCPL,
      "Sales Enablement":
        allocated["Sales Enablement"] / costs.enablementPerContact,
      "Market Intelligence":
        allocated["Market Intelligence"] / costs.intelPerSignal,
    };
  }, [allocated, costs]);

  // ---- Chart Data Preparation ----
  const pieData = [
    { name: "Brand", value: allocated.Brand },
    { name: "Demand Creation", value: allocated["Demand Creation"] },
    { name: "Market Intelligence", value: allocated["Market Intelligence"] },
    { name: "Sales Enablement", value: allocated["Sales Enablement"] },
  ];

  const barData = [
    { name: "Brand", value: outputs.Brand / 1000, label: "Impressions (k)" }, // Visual divide by 1000
    {
      name: "Demand Creation",
      value: outputs["Demand Creation"],
      label: "Leads",
    },
    {
      name: "Sales Enablement",
      value: outputs["Sales Enablement"],
      label: "Contacts",
    },
    {
      name: "Market Intelligence",
      value: outputs["Market Intelligence"],
      label: "Signals",
    },
  ];

  // Helper for currency formatting
  const fmtMoney = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  const fmtNum = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

  return (
    <div className="pmix-grid">
      {/* ---- Left Controls ---- */}
      <aside className="pmix-controls">
        {/* Budget */}
        <div className="pmix-group">
          <h4>Quarterly Budget</h4>
          <input
            type="number"
            className="input"
            style={{ width: "100%" }}
            value={budget}
            onChange={(e) => handleBudgetChange(e.target.value)}
          />
        </div>

        {/* Mix Config */}
        <div className="pmix-group">
          <h4>Mix (%)</h4>
          {Object.keys(mix).map((key) => (
            <div className="pmix-row" key={key}>
              <div className="pmix-row-left">
                <div className="dot" style={{ background: COLORS[key] }} />
                <span style={{ fontSize: "13px", fontWeight: 500 }}>{key}</span>
              </div>
              <div className="pmix-right pmix-money">
                <input
                  type="number"
                  className="input pct"
                  value={mix[key]}
                  onChange={(e) => handleMixChange(key, e.target.value)}
                />
                <span className="pct-symb">%</span>
                <span
                  style={{
                    minWidth: "70px",
                    textAlign: "right",
                    fontSize: "13px",
                  }}
                >
                  {fmtMoney(budget * (mix[key] / 100))}
                </span>
              </div>
            </div>
          ))}
          <small style={{ display: "block", marginTop: "8px" }}>
            Calculates automatically based on budget.
          </small>
        </div>

        {/* Unit Costs */}
        <div className="pmix-group">
          <h4>Unit Costs</h4>
          <div className="pmix-row">
            <label>Brand CPM (per 1,000)</label>
            <input
              type="number"
              className="input pmix-right"
              value={costs.brandCPM}
              onChange={(e) => handleCostChange("brandCPM", e.target.value)}
            />
          </div>
          <div className="pmix-row">
            <label>Demand CPL</label>
            <input
              type="number"
              className="input pmix-right"
              value={costs.demandCPL}
              onChange={(e) => handleCostChange("demandCPL", e.target.value)}
            />
          </div>
          <div className="pmix-row">
            <label>Enablement $ / contact</label>
            <input
              type="number"
              className="input pmix-right"
              value={costs.enablementPerContact}
              onChange={(e) =>
                handleCostChange("enablementPerContact", e.target.value)
              }
            />
          </div>
          <div className="pmix-row">
            <label>Market Intel $ / signal</label>
            <input
              type="number"
              className="input pmix-right"
              value={costs.intelPerSignal}
              onChange={(e) =>
                handleCostChange("intelPerSignal", e.target.value)
              }
            />
          </div>
        </div>

        {/* Quarter Budgets */}
        <div className="pmix-group">
          <h4>Quarter Budgets</h4>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="checkbox"
              checked={lockQuarters}
              onChange={(e) => setLockQuarters(e.target.checked)}
              id="lockQ"
            />
            <label
              htmlFor="lockQ"
              style={{ marginLeft: "8px", fontSize: "13px" }}
            >
              Use same as Quarterly Budget
            </label>
          </div>
          {["q1", "q2", "q3", "q4"].map((q) => (
            <div className="pmix-row" key={q}>
              <label style={{ textTransform: "uppercase" }}>{q}</label>
              <input
                type="number"
                className="input pmix-right"
                value={quarters[q]}
                disabled={lockQuarters}
                onChange={(e) =>
                  setQuarters({ ...quarters, [q]: Number(e.target.value) })
                }
              />
            </div>
          ))}
        </div>
      </aside>

      {/* ---- Right Visuals ---- */}
      <main>
        <div className="card">
          <div className="card-header">
            <h3>Program Mix Planner</h3>
            <div className="sub">
              Set a quarterly budget, adjust mix and unit costs, and preview
              outputs.
            </div>
          </div>

          <div
            className="viz-block"
            style={{ height: "300px", display: "flex" }}
          >
            <div style={{ flex: 1, height: "100%" }}>
              <h4 style={{ fontSize: "14px", marginBottom: "0" }}>Mix</h4>
              <ResponsiveContainer width="99%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[entry.name]}
                        stroke="none"
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => fmtMoney(val)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="viz-block" style={{ height: "320px" }}>
            <h4 style={{ fontSize: "14px" }}>Quarterly Outputs</h4>
            <ResponsiveContainer width="99%" height="100%">
              <BarChart
                data={barData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: "#f3f4f6" }}
                  formatter={(val, name, props) => [
                    fmtNum(val),
                    props.payload.label,
                  ]}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="badge">
              Brand shows impressions x1k; others are raw units.
            </div>
          </div>

          {/* BUDGET BY QUARTER CHART REMOVED */}
        </div>

        {/* ---- Summary Table ---- */}
        <div className="pmix-summary table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="col-family">Program Family</th>
                <th className="col-num right">Mix %</th>
                <th className="col-num right">Allocated</th>
                <th className="col-out right">Output</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="family">Brand</td>
                <td className="right num">{mix.Brand}%</td>
                <td className="right num">{fmtMoney(allocated.Brand)}</td>
                <td className="right out num">
                  {fmtNum(outputs.Brand)}{" "}
                  <span className="unit">impressions</span>
                </td>
              </tr>
              <tr>
                <td className="family">Demand Creation</td>
                <td className="right num">{mix["Demand Creation"]}%</td>
                <td className="right num">
                  {fmtMoney(allocated["Demand Creation"])}
                </td>
                <td className="right out num">
                  {fmtNum(outputs["Demand Creation"])}{" "}
                  <span className="unit">leads</span>
                </td>
              </tr>
              <tr>
                <td className="family">Sales Enablement</td>
                <td className="right num">{mix["Sales Enablement"]}%</td>
                <td className="right num">
                  {fmtMoney(allocated["Sales Enablement"])}
                </td>
                <td className="right out num">
                  {fmtNum(outputs["Sales Enablement"])}{" "}
                  <span className="unit">contacts</span>
                </td>
              </tr>
              <tr>
                <td className="family">Market Intelligence</td>
                <td className="right num">{mix["Market Intelligence"]}%</td>
                <td className="right num">
                  {fmtMoney(allocated["Market Intelligence"])}
                </td>
                <td className="right out num">
                  {fmtNum(outputs["Market Intelligence"])}{" "}
                  <span className="unit">signals</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default ProgramMixPlanner;
